import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/DataManagementPage.css';
import PaginationNav from '../components/PaginationNav';
import DataManagementItem from '../components/DataManagementItem';
import EntryData from '../components/EntryData';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useGroup } from '../contexts/GroupContext';
import GroupSelector from '../components/GroupSelector';
import {
  deserializeRawFromEditing,
  looksLikeJsonStructure,
  serializeRawForEditing,
  valueToEditableText,
} from '../utils/entryFieldValues';

/** HTTP 404 or API "Not Found" for group/suffix routes — usually cleared ingestion or missing horizon. */
function isIngestionDataMissingResponse(response, body) {
  if (response && response.status === 404) return true;
  if (!body || typeof body !== 'object') return false;
  if (body.success === true) return false;
  if (body.error === 'Not Found') return true;
  const m = String(body.message || '');
  if (/Endpoint .+ not found/i.test(m)) return true;
  if (/suffixes\/\/groups/i.test(m)) return true;
  return false;
}

const INGESTION_DATA_MISSING_MESSAGE = {
  type: 'info',
  title: 'Ingestion data unavailable',
  text:
    'No data was found for this horizon and group. The ingestion may have been cleared—re-run ingestion for this source or choose another group.',
};

function DataManagementPage() {
  const { appendGroupToUrl, currentGroup, setCurrentGroup, buildGroupApiUrl, activeSuffix, groups, loading: groupsLoading } = useGroup();
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  // page size cookie utilities
  const getCookie = (name) => {
    try {
      // Safely escape cookie name for regex
      const escaped = String(name).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  };
  const setCookie = (name, value, days = 365) => {
    try {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/`;
    } catch {}
  };
  const allowedPageSizes = [10, 20, 50, 100];
  const initialPageSize = (() => {
    const fromCookie = parseInt(getCookie('dataManagementPageSize') || '', 10);
    if (allowedPageSizes.includes(fromCookie)) return fromCookie;
    return 50;
  })();
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: initialPageSize,
    totalEntries: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [filters, setFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState({});
  const [availableFields, setAvailableFields] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedFields, setExpandedFields] = useState({});
  const [promptButtons, setPromptButtons] = useState([]);
  const [message, setMessage] = useState(null);
  const [executingPromptId, setExecutingPromptId] = useState(null);
  const promptsLoadedRef = useRef(false);
  const promptsLoadingRef = useRef(false);
  // Track last group used for loading prompts so we reload when the ingestion group changes
  const lastGroupForPromptsRef = useRef(null);
  // De-dup and cancellation for main query loading
  const listAbortRef = useRef(null);
  const listInFlightKeyRef = useRef(null);
  // Abstraction: actions per entry id so header actions are separated from the data object
  const actionsByIdRef = useRef(new Map());
  // Outputs edit state (split into key/value rows)
  const [editingOutputs, setEditingOutputs] = useState([]);
  // Snapshot of raw field types before stringifying for the edit form
  const editingOriginalRawRef = useRef({});
  // Unified keys for display/editing
  const [unifiedRawKeys, setUnifiedRawKeys] = useState([]);
  const [unifiedOutputKeys, setUnifiedOutputKeys] = useState([]);
  // Track last loaded keys for group/suffix to avoid duplicate fetch on mount/StrictMode
  const keysLoadedKeyRef = useRef(null);

  // Get field names (exclude system fields)
  const getFieldNames = (entry) => {
    const systemFields = ['id', 'created_at', 'updated_at', 'schema_version'];
    const isVisibleKey = (k) => typeof k === 'string' && !k.startsWith('_');
    return Object.keys(entry || {}).filter(key => isVisibleKey(key) && !systemFields.includes(key));
  };

  // Load data entries
  const loadEntries = useCallback(async (pageNum, filtersToUse = null) => {
    // If there's no current group, don't attempt to load entries
    if (!currentGroup) {
      console.log('[DataManagementPage] No current group selected, skipping load');
      setEntries([]);
      setPagination({
        page: 0,
        pageSize: pageSize,
        totalEntries: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
      });
      setLoading(false);
      return;
    }

    // Wait for horizon/suffix from GroupContext to avoid `suffixes//groups/...` and spurious 404s
    if (groupsLoading) {
      return;
    }

    if (!activeSuffix) {
      setEntries([]);
      setPagination({
        page: 0,
        pageSize,
        totalEntries: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      });
      setMessage(INGESTION_DATA_MISSING_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    const filtersForRequest = filtersToUse !== null ? filtersToUse : activeFilters;
    try {
      // Build a request key for de-duplication
      const groupKey = currentGroup && (currentGroup.slug || currentGroup.groupSlug || currentGroup.id || currentGroup);
      const reqKey = `${groupKey}|${pageNum}|${pageSize}|${JSON.stringify(filtersForRequest)}`;

      // If the same request is already in flight, skip
      if (listInFlightKeyRef.current === reqKey) {
        setLoading(false);
        return;
      }

      // Abort previous in-flight if different
      if (listAbortRef.current) {
        try { listAbortRef.current.abort(); } catch {}
      }
      const controller = new AbortController();
      listAbortRef.current = controller;
      listInFlightKeyRef.current = reqKey;

      // Use new query endpoint for entries
      const authHeaders = await getAuthHeaders();
      const response = await fetch(buildGroupApiUrl('/entries/query'), {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filters: filtersForRequest,
          limit: pageSize,
          offset: Math.max(0, pageNum) * pageSize,
          orderBy: 'created_at',
          orderDirection: 'desc'
        }),
        signal: controller.signal,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Normalize list payload across possible shapes: data, items, entries
          const list = Array.isArray(data.data)
            ? data.data
            : (Array.isArray(data.items)
              ? data.items
              : (Array.isArray(data.entries) ? data.entries : []));
          // New API returns `count` (items in current page) and `totalCount` (all matching items)
          const pageCount = typeof data.count === 'number' ? data.count : list.length;
          const totalCount = typeof data.totalCount === 'number'
            ? data.totalCount
            : (typeof data.pagination?.totalEntries === 'number' ? data.pagination.totalEntries : pageCount);
          setEntries(list);
          const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
          const offset = Math.max(0, pageNum) * pageSize;
          setPagination({
            page: pageNum,
            pageSize,
            totalEntries: totalCount,
            totalPages,
            // Determine next/prev using totalCount and current page size actually returned
            hasNextPage: offset + pageCount < totalCount,
            hasPreviousPage: pageNum > 0
          });
          setMessage(null);
        } else if (isIngestionDataMissingResponse(response, data)) {
          setEntries([]);
          setPagination({
            page: 0,
            pageSize,
            totalEntries: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          });
          setMessage(INGESTION_DATA_MISSING_MESSAGE);
        } else {
          setMessage({
            type: 'error',
            title: 'Error:',
            text: data.error || 'Failed to load entries'
          });
        }
      } else {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (_) {
          /* non-JSON error body */
        }
        if (isIngestionDataMissingResponse(response, errorData)) {
          setEntries([]);
          setPagination({
            page: 0,
            pageSize,
            totalEntries: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          });
          setMessage(INGESTION_DATA_MISSING_MESSAGE);
        } else {
          setMessage({
            type: 'error',
            title: 'Error:',
            text: errorData.error || errorData.message || 'Failed to load entries'
          });
        }
      }
    } catch (error) {
      // Ignore abort errors from rapid state changes or StrictMode double-invoke
      if (!(error && (error.name === 'AbortError' || error.code === 20))) {
        console.error('[DataManagementPage] Error loading entries:', error);
        setMessage({
          type: 'error',
          title: 'Error:',
          text: error.message || 'Failed to load entries'
        });
      }
    } finally {
      // Clear in-flight key when this request finishes (only if it is the latest)
      const groupKey = currentGroup && (currentGroup.slug || currentGroup.groupSlug || currentGroup.id || currentGroup);
      const latestKey = `${groupKey}|${pageNum}|${pageSize}|${JSON.stringify(filtersForRequest)}`;
      if (listInFlightKeyRef.current === latestKey) {
        listInFlightKeyRef.current = null;
      }
      setLoading(false);
    }
  }, [activeFilters, pageSize, buildGroupApiUrl, currentGroup, activeSuffix, groupsLoading]);

  // Load unified keys for the current group to show empty columns and edit all keys
  const loadUnifiedKeys = useCallback(async () => {
    try {
      if (!currentGroup || groupsLoading || !activeSuffix) return;
      const loadKey = `${activeSuffix}::${currentGroup}`;
      if (keysLoadedKeyRef.current === loadKey) {
        return; // already loaded for this group/suffix
      }
      const authHeaders = await getAuthHeaders();
      const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(activeSuffix)}/groups/${encodeURIComponent(currentGroup)}/keys`;
      const resp = await fetch(url, { method: 'GET', headers: authHeaders });
      if (!resp.ok) return;
      const json = await resp.json().catch(() => ({}));
      const isVisibleKey = (k) => typeof k === 'string' && !k.startsWith('_');
      const rawKeys = Array.isArray(json?.data?.raw) ? json.data.raw : [];
      const outExisting = Array.isArray(json?.data?.output?.existing) ? json.data.output.existing : [];
      const outDefined = Array.isArray(json?.data?.output?.defined) ? json.data.output.defined : [];
      const outSet = Array.from(new Set([...(outExisting || []), ...(outDefined || [])].filter((k) => k && isVisibleKey(k))));
      setUnifiedRawKeys(Array.from(new Set([...(rawKeys || [])].filter((k) => k && isVisibleKey(k)))));
      setUnifiedOutputKeys(outSet);
      keysLoadedKeyRef.current = loadKey;
    } catch (_) {
      // ignore; leave empty
    }
  }, [activeSuffix, currentGroup, groupsLoading]);

  // Load available field names from API
  const loadAvailableFields = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/data-management/fields`), {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.fields) {
          setAvailableFields(data.fields);
        }
      }
    } catch (error) {
      console.error('[DataManagementPage] Error loading available fields:', error);
    }
  };

  // Initial load and reload when ingestion group/suffix changes
  useEffect(() => {
    loadAvailableFields();
    loadUnifiedKeys();
    loadEntries(0, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup, activeSuffix, groupsLoading]); // reload when horizon context is ready or group/suffix changes

  // Abort any in-flight list request on unmount
  useEffect(() => {
    return () => {
      if (listAbortRef.current) {
        try { listAbortRef.current.abort(); } catch {}
      }
    };
  }, []);

  // Reload when pageSize changes
  useEffect(() => {
    // Reset to first page when page size changes
    loadEntries(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  // If unified keys arrive after opening the editor (e.g., after switching ingestion groups),
  // update the edit form to reflect the newly fetched keys.
  useEffect(() => {
    if (!editingEntry) return;
    const isVisibleKey = (k) => typeof k === 'string' && !k.startsWith('_');

    // Rebuild expanded raw fields using unified raw keys if available
    const rawObj = editingEntry && editingEntry.raw && typeof editingEntry.raw === 'object' ? editingEntry.raw : {};
    const rawFieldNames = (unifiedRawKeys && unifiedRawKeys.length > 0)
      ? unifiedRawKeys.filter(isVisibleKey)
      : Object.keys(rawObj).filter(isVisibleKey);
    const rebuiltExpanded = {};
    rawFieldNames.forEach(field => {
      const value = rawObj[field];
      const strValue = valueToEditableText(value);
      rebuiltExpanded[field] = strValue.length > 200 ? 3 : 1;
    });
    setExpandedFields(rebuiltExpanded);

    // Rebuild outputs rows using unified output keys union existing output keys
    const outputObj = editingEntry && editingEntry.output && typeof editingEntry.output === 'object' ? editingEntry.output : {};
    const outKeysSet = new Set([...(unifiedOutputKeys || []), ...Object.keys(outputObj || {})].filter(isVisibleKey));
    const rows = Array.from(outKeysSet).map(k => ({
      key: k,
      value: valueToEditableText(outputObj[k]),
      isNull: outputObj[k] === null,
    }));
    setEditingOutputs(rows);
  }, [unifiedRawKeys, unifiedOutputKeys, editingEntry]);

  useEffect(() => {
    // Reload prompt buttons when ingestion group changes, but avoid duplicate overlapping calls
    if ((promptsLoadedRef.current && !promptsLoadingRef.current) && lastGroupForPromptsRef.current === (currentGroup || '')) {
      return;
    }
    promptsLoadedRef.current = true;
    promptsLoadingRef.current = true;
    lastGroupForPromptsRef.current = (currentGroup || '');

    const loadPromptButtons = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/prompts`), {
          method: 'POST',
          headers,
          body: JSON.stringify({ page_location: 'management' }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.prompts)) {
            const group = currentGroup || '';
            const filtered = data.prompts
              .filter(p => (p.ingestion_group_slug || '') === group)
              .filter(p => (p.page_location === 'management'));
            setPromptButtons(filtered);
          } else {
            setPromptButtons([]);
          }
        } else {
          setPromptButtons([]);
        }
      } catch (error) {
        console.error('[DataManagementPage] Error loading prompt buttons:', error);
        promptsLoadedRef.current = false; // Reset on error so it can retry
        setPromptButtons([]);
      } finally {
        promptsLoadingRef.current = false;
      }
    };

    loadPromptButtons();
  }, [appendGroupToUrl, currentGroup]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      loadEntries(newPage);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value === '' || value === null || value === undefined) {
        delete newFilters[field];
      } else {
        newFilters[field] = value;
      }
      return newFilters;
    });
  };

  const handleSearch = () => {
    const newActiveFilters = { ...filters };
    setActiveFilters(newActiveFilters);
    // Reset to first page when searching, pass filters directly to avoid stale state
    loadEntries(0, newActiveFilters);
  };

  const handleResetFilters = () => {
    setFilters({});
    setActiveFilters({});
    // Reset to first page and reload with empty filters (all entries)
    loadEntries(0, {});
  };

  const handleEdit = useCallback((entry) => {
    const rawObj = entry && typeof entry.raw === 'object' && entry.raw !== null && !Array.isArray(entry.raw) ? { ...entry.raw } : {};
    const outputObj = entry && typeof entry.output === 'object' && entry.output !== null && !Array.isArray(entry.output) ? { ...entry.output } : {};
    editingOriginalRawRef.current = rawObj;
    // Textareas need string values; nested objects/arrays become pretty-printed JSON
    setEditingEntry({
      id: entry.id,
      reviewed: !!entry.reviewed,
      approved: !!entry.approved,
      raw: serializeRawForEditing(rawObj),
      output: outputObj,
    });
    // Initialize expanded fields for raw keys
    const isVisibleKey = (k) => typeof k === 'string' && !k.startsWith('_');
    const fieldNames = (unifiedRawKeys && unifiedRawKeys.length > 0)
      ? unifiedRawKeys.filter(isVisibleKey)
      : Object.keys(rawObj).filter(isVisibleKey);
    const initialExpanded = {};
    fieldNames.forEach(field => {
      const strValue = valueToEditableText(rawObj[field]);
      initialExpanded[field] = strValue.length > 200 ? 3 : 1;
    });
    setExpandedFields(initialExpanded);
    // Initialize outputs editor using union of keys
    const outKeysSet = new Set([...(unifiedOutputKeys || []), ...Object.keys(outputObj || {})].filter(isVisibleKey));
    const rows = Array.from(outKeysSet).map(k => ({
      key: k,
      value: valueToEditableText(outputObj[k]),
      isNull: outputObj[k] === null,
    }));
    setEditingOutputs(rows);
  }, [unifiedRawKeys, unifiedOutputKeys]);

  const handleAddNew = useCallback(() => {
    const rawObj = {};
    const outputObj = {};
    editingOriginalRawRef.current = {};
    setEditingEntry({ id: null, reviewed: false, approved: false, raw: rawObj, output: outputObj });
    // Expanded fields default lengths
    const isVisibleKey = (k) => typeof k === 'string' && !k.startsWith('_');
    const fieldNames = unifiedRawKeys && unifiedRawKeys.length > 0 ? unifiedRawKeys.filter(isVisibleKey) : [];
    const initialExpanded = {};
    fieldNames.forEach(field => { initialExpanded[field] = 1; });
    setExpandedFields(initialExpanded);
    // Outputs editor pre-populated with unified output keys
    const rows = (unifiedOutputKeys || []).filter(isVisibleKey).map(k => ({ key: k, value: '', isNull: false }));
    setEditingOutputs(rows);
  }, [unifiedRawKeys, unifiedOutputKeys]);

  const createFromUrlRef = useRef(false);
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const group = params.get('group');
      if (group) setCurrentGroup(group);
    } catch (_) { /* ignore */ }
  }, [setCurrentGroup]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('create') !== '1' || createFromUrlRef.current || !currentGroup) return;
      createFromUrlRef.current = true;
      handleAddNew();
      params.delete('create');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    } catch (_) { /* ignore */ }
  }, [currentGroup, handleAddNew]);

  const handleSaveEdit = async () => {
    if (!editingEntry) return;

    try {
      // Determine if this is an update (existing entry) or create (new entry)
      let savedId = editingEntry.id;

      // Prepare payload for create/update with raw and flags
      // When editing an existing entry, exclude any `_file*` fields from the raw payload
      let restoredRaw;
      try {
        restoredRaw = deserializeRawFromEditing(editingEntry.raw || {}, editingOriginalRawRef.current || {});
      } catch (parseErr) {
        setMessage({
          type: 'error',
          title: 'Invalid JSON:',
          text: parseErr?.message || 'One or more fields contain invalid JSON.',
        });
        return;
      }
      const filteredRaw = savedId
        ? Object.fromEntries(Object.entries(restoredRaw).filter(([k]) => !(typeof k === 'string' && k.startsWith('_file'))))
        : restoredRaw;

      const payload = {
        raw: filteredRaw,
        reviewed: !!editingEntry.reviewed,
        approved: !!editingEntry.approved,
      };
      let response;
      const headers = await getAuthHeaders();
      if (savedId) {
        response = await fetch(buildGroupApiUrl(`/entries/${encodeURIComponent(savedId)}`), {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(buildGroupApiUrl('/entries'), {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (!savedId) {
            savedId = data?.data?.id || data?.entry?.id;
          }
          // After entry update, also apply outputs via batch API
          const items = (editingOutputs || [])
            .map((row) => {
              let value = row && row.isNull ? null : row?.value;
              if (typeof value === 'string' && looksLikeJsonStructure(value)) {
                try { value = JSON.parse(value); } catch { /* keep as string */ }
              }
              return {
                key: String(row.key || '').trim(),
                value,
              };
            })
            .filter((r) => r.key);

          if (items.length > 0) {
            try {
              const headers = await getAuthHeaders();
              const resp = await fetch(appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/data-output/batch`), {
                method: 'POST',
                headers,
                body: JSON.stringify(items.map(it => ({ id: savedId, key: it.key, value: it.value }))),
              });
              if (resp.ok) {
                const out = await resp.json().catch(() => ({}));
                const results = Array.isArray(out?.results) ? out.results : [];
                const successCount = results.filter(r => r && r.upserted).length;
                const errorItems = results.filter(r => r && r.error);
                const errorCount = errorItems.length;
                // Build a compact list of per-item error messages so users can see what failed
                const errorDetails = errorCount > 0
                  ? errorItems.slice(0, 10).map(r => {
                      const k = r?.key || '(unknown key)';
                      const msg = typeof r.error === 'string' ? r.error : JSON.stringify(r.error);
                      return `- ${k}: ${msg}`;
                    }).join('\n')
                  : '';

                const baseText = results.length > 0
                  ? `Entry updated. Outputs: ${successCount} succeeded, ${errorCount} failed.`
                  : 'Entry updated and outputs applied.';

                setMessage({
                  type: errorCount > 0 ? 'error' : 'success',
                  title: errorCount > 0 ? 'Saved with output errors:' : 'Success:',
                  text: errorCount > 0 ? `${baseText}\n\nErrors:\n${errorDetails}${errorCount > 10 ? '\n- (more errors omitted)' : ''}` : baseText
                });

                if (errorCount === 0) {
                  setEditingEntry(null);
                  loadEntries(pagination.page);
                  setTimeout(() => setMessage(null), 3000);
                }
              } else {
                const err = await resp.json().catch(() => ({}));
                setMessage({ type: 'error', title: 'Saved with output error:', text: err.error || 'Failed to apply outputs.' });
              }
            } catch (e) {
              setMessage({ type: 'error', title: 'Saved with output error:', text: e.message || 'Failed to apply outputs.' });
            }
          } else {
            // No outputs to apply
            setMessage({ type: 'success', title: 'Success:', text: 'Entry updated successfully' });
            setEditingEntry(null);
            loadEntries(pagination.page);
            setTimeout(() => setMessage(null), 3000);
          }
        } else {
          setMessage({
            type: 'error',
            title: 'Error:',
            text: data.error || 'Failed to update entry'
          });
        }
      } else {
        const errorData = await response.json();
        setMessage({
          type: 'error',
          title: 'Error:',
          text: errorData.error || 'Failed to update entry'
        });
      }
    } catch (error) {
      console.error('[DataManagementPage] Error updating entry:', error);
      setMessage({
        type: 'error',
        title: 'Error:',
        text: error.message || 'Failed to update entry'
      });
    }
  };

  const updateOutputRow = (index, patch) => {
    setEditingOutputs(prev => prev.map((row, i) => i === index ? { ...row, ...patch } : row));
  };

  const handleDelete = (entry) => {
    setDeleteConfirm(entry);
  };

  // Helper to find the most recent entry by id
  const findEntryById = useCallback((id) => {
    return entries.find((e) => e && e.id === id);
  }, [entries]);

  // Rebuild the actions map whenever the entry list changes so actions are
  // decoupled from the entry object and can be referenced later.
  useEffect(() => {
    const map = new Map();
    for (const e of entries) {
      if (!e || e.id == null) continue;
      const id = e.id;
      map.set(id, {
        edit: () => {
          const latest = findEntryById(id) || e;
          handleEdit(latest);
        },
        delete: () => {
          const latest = findEntryById(id) || e;
          handleDelete(latest);
        }
      });
    }
    actionsByIdRef.current = map;
  }, [entries, findEntryById, handleEdit]);

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(buildGroupApiUrl(`/entries/${encodeURIComponent(deleteConfirm.id)}`), {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessage({
            type: 'success',
            title: 'Success:',
            text: 'Entry deleted successfully'
          });
          setDeleteConfirm(null);
          loadEntries(pagination.page);
          setTimeout(() => setMessage(null), 3000);
        } else {
          setMessage({
            type: 'error',
            title: 'Error:',
            text: data.error || 'Failed to delete entry'
          });
        }
      } else {
        const errorData = await response.json();
        setMessage({
          type: 'error',
          title: 'Error:',
          text: errorData.error || 'Failed to delete entry'
        });
      }
    } catch (error) {
      console.error('[DataManagementPage] Error deleting entry:', error);
      setMessage({
        type: 'error',
        title: 'Error:',
        text: error.message || 'Failed to delete entry'
      });
    }
  };

  const toggleFieldExpansion = (fieldName) => {
    setExpandedFields(prev => {
      const current = prev[fieldName] || 3;
      if (current === 3) {
        return { ...prev, [fieldName]: 10 };
      } else {
        return { ...prev, [fieldName]: 3 };
      }
    });
  };


  // Filter dropdown: same keys as entry display (unified raw + output from /keys), then API, then row scan
  const getAllFieldNames = () => {
    const isVisibleKey = (k) => typeof k === 'string' && !k.startsWith('_');
    const fromUnified = [
      ...(unifiedRawKeys || []).filter(isVisibleKey),
      ...(unifiedOutputKeys || []).filter(isVisibleKey),
    ];
    if (currentGroup && fromUnified.length > 0) {
      return Array.from(new Set(fromUnified)).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
    }
    if (availableFields.length > 0) {
      return availableFields;
    }
    const fieldSet = new Set();
    entries.forEach(entry => {
      getFieldNames(entry).forEach(field => fieldSet.add(field));
    });
    return Array.from(fieldSet).sort();
  };

  return (
    <div className="data-management-page">
      {message && (
        <div className="result-container" style={{ marginBottom: '20px' }}>
          <div className={`alert alert-${message.type}`}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : message.type === 'error' ? 'exclamation-circle' : 'info-circle'}`}></i>
            <div>
              {message.title && <strong>{message.title}</strong>}
              {message.title && ' '}
              {message.text}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="header-content" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ marginRight: 'auto' }}><i className="fas fa-database"></i> Data Management</h2>
            <GroupSelector compact />
            <button className="btn btn-primary" onClick={handleAddNew}>
              <i className="fas fa-plus"></i> Add Entry
            </button>
          </div>
        </div>
        <p className="section-description">
          View and manage approved data entries. Search, filter, edit, and delete entries as needed.
        </p>

        {/* Filters Section */}
        <div className="data-controls">
          <div className="filters-header">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className={`fas fa-${showFilters ? 'chevron-up' : 'chevron-down'}`}></i> {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            {Object.keys(filters).length > 0 && (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  <i className="fas fa-search"></i> Search
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleResetFilters}
                  disabled={loading}
                >
                  <i className="fas fa-undo"></i> Reset Filters
                </button>
              </>
            )}
          </div>

          {showFilters && (
            <div className="filters-panel">
              {getAllFieldNames().map(fieldName => (
                <div key={fieldName} className="form-group">
                  <label htmlFor={`filter-${fieldName}`}>{fieldName}:</label>
                  <input
                    type="text"
                    id={`filter-${fieldName}`}
                    value={filters[fieldName] || ''}
                    onChange={(e) => handleFilterChange(fieldName, e.target.value)}
                    placeholder={`Filter by ${fieldName}...`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Prompt Buttons */}
        {promptButtons.length > 0 && (
          <div className="prompt-buttons-section">
            <h3><i className="fas fa-magic"></i> Agent Workflows</h3>
            <div className="prompt-buttons">
              {promptButtons.map((prompt) => (
                <button
                  key={prompt.id}
                  className="btn btn-secondary prompt-button"
                  disabled={executingPromptId === prompt.id}
                  onClick={async () => {
                    try {
                      setExecutingPromptId(prompt.id);
                      setMessage(null);
                      const headers = await getAuthHeaders();
                      const resp = await fetch(appendGroupToUrl(`${API_BASE_URL}/casc/v1/api/prompt-executions`), {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({ prompt_id: prompt.id })
                      });
                      if (resp.ok) {
                        const data = await resp.json();
                        if (data && data.id) {
                          // Notify queue to refresh immediately
                          try {
                            window.dispatchEvent(new CustomEvent('executionRequestCreated', { detail: { id: data.id, prompt_id: prompt.id } }));
                          } catch {}
                          setMessage({
                            type: 'success',
                            title: 'Request created',
                            text: `A new execution request was created for "${prompt.name}". Track progress on Horizon Main under Prompt executions.`,
                          });
                        } else {
                          setMessage({ type: 'error', title: 'Unexpected response:', text: 'The server did not return a request id.' });
                        }
                      } else if (resp.status === 400) {
                        const err = await resp.json().catch(() => ({}));
                        setMessage({ type: 'error', title: 'Invalid request:', text: err.error || 'Invalid prompt id.' });
                      } else if (resp.status === 401) {
                        setMessage({ type: 'error', title: 'Unauthorized:', text: 'You must be signed in to execute prompts.' });
                      } else if (resp.status === 503) {
                        setMessage({ type: 'error', title: 'Service unavailable:', text: 'Please try again later.' });
                      } else {
                        const err = await resp.json().catch(() => ({}));
                        setMessage({ type: 'error', title: 'Error:', text: err.error || `Request failed with status ${resp.status}.` });
                      }
                    } catch (e) {
                      setMessage({ type: 'error', title: 'Network error:', text: e.message || 'Failed to create execution request.' });
                    } finally {
                      setExecutingPromptId(null);
                    }
                  }}
                >
                  {executingPromptId === prompt.id ? 'Creating...' : prompt.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pagination Controls - Top */}
        <PaginationNav
          position="top"
          page={pagination.totalPages > 0 ? pagination.page + 1 : 1}
          pageSize={pageSize}
          total={pagination.totalEntries}
          allowedPageSizes={allowedPageSizes}
          disabled={loading || pagination.totalPages === 0}
          onPageChange={(p1) => handlePageChange(p1 - 1)}
          onPageSizeChange={(sz) => {
            const next = allowedPageSizes.includes(sz) ? sz : pageSize;
            setPageSize(next);
            setCookie('dataManagementPageSize', String(next));
          }}
        />

        {/* Data Results */}
        <div className="data-results">
          {loading ? (
            <p className="no-results">Loading entries...</p>
          ) : !currentGroup || !groups || groups.length === 0 ? (
            <div className="no-results">
              <p><i className="fas fa-info-circle"></i> No ingestion groups available for this horizon.</p>
              <p style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
                To get started, go to <strong>Settings → Ingestion</strong> to create an ingestion group and associate it with this horizon.
              </p>
            </div>
          ) : entries.length === 0 ? (
            <p className="no-results">
              No approved entries found. {Object.keys(filters).length > 0 ? 'Try adjusting your filters.' : 'Wait for data to be processed and approved.'}
            </p>
          ) : (
            <div className="data-items-list">
              {entries.map((entry, index) => {
                const itemNumber = pagination.page * pagination.pageSize + index + 1;
                const actions = actionsByIdRef.current.get(entry.id) || {
                  edit: () => handleEdit(entry),
                  delete: () => handleDelete(entry)
                };
                return (
                  <DataManagementItem key={entry.id} number={itemNumber} actions={actions}>
                    <EntryData
                      entry={entry}
                      unifiedRawKeys={unifiedRawKeys}
                      unifiedOutputKeys={unifiedOutputKeys}
                      fileDownloadContext={
                        activeSuffix && currentGroup
                          ? { suffix: activeSuffix, groupSlug: currentGroup }
                          : null
                      }
                    />
                  </DataManagementItem>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination Controls - Bottom */}
        <PaginationNav
          position="bottom"
          page={pagination.totalPages > 0 ? pagination.page + 1 : 1}
          pageSize={pageSize}
          total={pagination.totalEntries}
          allowedPageSizes={allowedPageSizes}
          disabled={loading || pagination.totalPages === 0}
          onPageChange={(p1) => handlePageChange(p1 - 1)}
          onPageSizeChange={(sz) => {
            const next = allowedPageSizes.includes(sz) ? sz : pageSize;
            setPageSize(next);
            setCookie('dataManagementPageSize', String(next));
          }}
        />
      </div>

      {/* Edit Modal */}
      {editingEntry && (
        <div className="modal show">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Entry</h2>
              <span className="close" onClick={() => setEditingEntry(null)}>&times;</span>
            </div>
            <div className="modal-body">
              <div className="edit-fields">
                {/* Reviewed/Approved toggles */}
                {['reviewed','approved'].map(fieldName => (
                  <div key={fieldName} className="edit-field">
                    <label htmlFor={`edit-${fieldName}`}>{fieldName}:</label>
                    <select
                      id={`edit-${fieldName}`}
                      value={editingEntry[fieldName] ? 'true' : 'false'}
                      onChange={(e) => setEditingEntry(prev => ({ ...prev, [fieldName]: e.target.value === 'true' }))}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                ))}

                {/* Raw fields based on unified keys (hide underscore-prefixed keys) */}
                {(unifiedRawKeys && unifiedRawKeys.length > 0 ? unifiedRawKeys : Object.keys(editingEntry.raw || {}))
                  .filter((k) => typeof k === 'string' && !k.startsWith('_'))
                  .map(fieldName => {
                  const fieldValue = (editingEntry.raw || {})[fieldName];
                  const strValue = valueToEditableText(fieldValue);
                  const isLongField = strValue.length > 200 || strValue.includes('\n');
                  const defaultRows = isLongField ? 3 : 1;
                  const rows = expandedFields[fieldName] || defaultRows;
                  const isExpanded = rows === 10;
                  const shouldShowExpand = isLongField;

                  return (
                    <div key={fieldName} className="edit-field">
                      <label htmlFor={`edit-${fieldName}`}>{fieldName}:</label>
                      <>
                        <textarea
                          id={`edit-${fieldName}`}
                          rows={rows}
                          value={strValue}
                          onChange={(e) => setEditingEntry(prev => ({
                            ...prev,
                            raw: { ...(prev.raw || {}), [fieldName]: e.target.value }
                          }))}
                          className={isExpanded ? 'expanded' : ''}
                          spellCheck={false}
                        />
                        {shouldShowExpand && rows === 3 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary expand-btn"
                            onClick={() => toggleFieldExpansion(fieldName)}
                          >
                            <i className="fas fa-chevron-down"></i> Expand
                          </button>
                        )}
                        {shouldShowExpand && rows === 10 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary expand-btn"
                            onClick={() => toggleFieldExpansion(fieldName)}
                          >
                            <i className="fas fa-chevron-up"></i> Collapse
                          </button>
                        )}
                      </>
                    </div>
                  );
                })}
              </div>

              {/* Outputs Editor */}
              <div className="outputs-editor" style={{ marginTop: 16 }}>
                <h3 style={{ marginBottom: 8 }}>Outputs</h3>
                {editingOutputs.length === 0 && (
                  <p className="no-results" style={{ marginTop: 6 }}>No outputs.</p>
                )}
                {editingOutputs.map((row, idx) => {
                  return (
                    <div key={idx} className="edit-field" style={{ alignItems: 'flex-start' }}>
                      <label style={{ minWidth: 60 }}>{row.key}:</label>
                      <textarea
                        rows={String(row.value ?? '').includes('\n') || String(row.value ?? '').length > 200 ? 3 : 1}
                        value={valueToEditableText(row.value)}
                        onChange={(e) => updateOutputRow(idx, { value: e.target.value })}
                        style={{ flex: 2, marginRight: 8 }}
                        spellCheck={false}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingEntry(null)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveEdit}>
                <i className="fas fa-save"></i> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal show">
          <div className="modal-content delete-confirm">
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <span className="close" onClick={() => setDeleteConfirm(null)}>&times;</span>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this entry?</p>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
                No, Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                <i className="fas fa-trash"></i> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataManagementPage;
